export const API_BASE = (
  import.meta.env.VITE_API_URL || "/api/v1"
).replace(/\/$/, "");

export class AuthError extends Error {
  constructor() {
    super("Authentication required");
    this.name = "AuthError";
  }
}

export class ApiError extends Error {
  status: number;
  fields?: Record<string, string>;
  errorBody?: { code?: string; message?: string; dependencies?: { type: string; count: number }[] };

  constructor(message: string, status: number, fields?: Record<string, string>, errorBody?: ApiError['errorBody']) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    if (fields) {
      const normalized: Record<string, string> = {};
      for (const [key, value] of Object.entries(fields)) {
        if (Array.isArray(value)) {
          const first = value.find((v) => typeof v === 'string' && v.length > 0);
          if (first) normalized[key] = first;
        } else if (typeof value === 'string') {
          normalized[key] = value;
        }
      }
      this.fields = normalized;
    }
    if (errorBody) this.errorBody = errorBody;
  }
}

async function request<T>(url: string, options: RequestInit = {}, tokenKey = "access_token"): Promise<T> {
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers: Record<string, string> = {
    // Multipart bodies must keep the browser-generated boundary header.
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers as Record<string, string> || {}),
  };

  const token = sessionStorage.getItem(tokenKey);
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (!res.ok) {
    if (res.status === 401) {
      const body = await res.json().catch(() => ({}));
      const message = typeof body.error === 'string' && body.error ? body.error : undefined;
      sessionStorage.removeItem(tokenKey);
      if (url.includes('/login')) {
        throw new Error(message || 'Invalid email or password.');
      }
      const loginPath = tokenKey === 'platform_access_token' ? '/super-admin/login' : '/login';
      if (!window.location.pathname.includes('/login')) {
        window.location.href = loginPath;
      }
      throw new AuthError();
    }
    const body = await res.json().catch(() => ({}));
    let message = `Request failed (${res.status})`;
    let errorBody: ApiError['errorBody'] | undefined;
    if (typeof body.error === 'string') {
      message = body.error;
    } else if (typeof body.error === 'object' && body.error !== null) {
      const eb = body.error as Record<string, unknown>;
      if (eb.code && typeof eb.code === 'string' && eb.message && typeof eb.message === 'string') {
        message = eb.message;
        errorBody = {
          code: eb.code,
          message: eb.message,
          dependencies: Array.isArray(eb.dependencies) ? eb.dependencies : undefined,
        };
      } else {
        const parts: string[] = [];
        for (const [key, msgs] of Object.entries(body.error)) {
          if (Array.isArray(msgs) && msgs.length > 0) {
            const label = key
              .split('.')
              .map(p => p.replace(/^\d+$/, ''))
              .filter(Boolean)
              .map(p => p.charAt(0).toUpperCase() + p.slice(1))
              .join(' > ');
            parts.push(`${label}: ${msgs.join(', ')}`);
          }
        }
        if (parts.length > 0) message = parts.join('; ');
      }
    } else if (body.error?.message) {
      message = body.error.message;
    } else if (body.message) {
      message = body.message;
    }
    throw new ApiError(message, res.status, body.fields && typeof body.fields === 'object' ? body.fields : undefined, errorBody);
  }

  return res.json();
}

export const api = {
  get: <T>(url: string) => request<T>(url),
  post: <T>(url: string, data?: unknown) =>
    request<T>(url, { method: 'POST', body: data !== undefined ? JSON.stringify(data) : undefined }),
  put: <T>(url: string, data: unknown) =>
    request<T>(url, { method: 'PUT', body: JSON.stringify(data) }),
  patch: <T>(url: string, data: unknown) =>
    request<T>(url, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: <T>(url: string, data?: unknown) =>
    request<T>(url, { method: 'DELETE', body: data !== undefined ? JSON.stringify(data) : undefined }),
  /** Multipart upload. Content-Type is set by the browser with the boundary. */
  upload: <T>(url: string, formData: FormData) =>
    request<T>(url, { method: 'POST', body: formData }),
};

const DEP_LABELS: Record<string, string> = {
  activities: 'Activities',
  requirements: 'Requirements',
  blocks: 'Blocks',
  actualLabour: 'Actual Labour records',
  attendanceRecords: 'Attendance records',
  lumpSumMandays: 'Lump-sum mandays',
  workers: 'Workers',
};

export function formatDeleteError(e: unknown, entityLabel: string): string {
  if (e instanceof ApiError && e.errorBody?.dependencies?.length) {
    const deps = e.errorBody.dependencies.map(d => {
      const label = DEP_LABELS[d.type] || d.type;
      return `${d.count} ${label}`;
    });
    if (deps.length === 1) {
      return `This ${entityLabel} is used by ${deps[0]}. Remove or replace those references before permanently deleting it.`;
    }
    return `This ${entityLabel} is currently used in:\n${deps.map(d => `\u2022 ${d}`).join('\n')}`;
  }
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return `Failed to delete ${entityLabel}.`;
}

/**
 * Fetch a private image through an authenticated endpoint and return a
 * revocable object URL. Used for photographs stored in R2, which can never be
 * loaded via a public URL. Callers must revoke the returned URL on cleanup.
 */
export async function fetchImageAsBlobUrl(url: string, tokenKey = "access_token"): Promise<string> {
  const headers: Record<string, string> = {};
  const token = sessionStorage.getItem(tokenKey);
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${url}`, { headers, credentials: 'include' });
  if (!res.ok) throw new ApiError('Unable to load image', res.status);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/** Platform (SUPER_ADMIN) API client. Uses its own session token. */
export const platformApi = {
  get: <T>(url: string, options: RequestInit = {}) => request<T>(url, options, "platform_access_token"),
  post: <T>(url: string, data?: unknown, options: RequestInit = {}) =>
    request<T>(url, { ...options, method: 'POST', body: data !== undefined ? JSON.stringify(data) : undefined }, "platform_access_token"),
  put: <T>(url: string, data: unknown) =>
    request<T>(url, { method: 'PUT', body: JSON.stringify(data) }, "platform_access_token"),
  patch: <T>(url: string, data: unknown) =>
    request<T>(url, { method: 'PATCH', body: JSON.stringify(data) }, "platform_access_token"),
  delete: <T>(url: string) => request<T>(url, { method: 'DELETE' }, "platform_access_token"),
};

/** Performs an audited write in support mode (requires X-Support-Write header). */
export const supportWrite = <T>(method: 'POST' | 'PUT' | 'PATCH' | 'DELETE', url: string, data?: unknown) =>
  request<T>(url, {
    method,
    body: data !== undefined ? JSON.stringify(data) : undefined,
    headers: { 'X-Support-Write': 'true' },
  }, "platform_access_token");
